/**
 * api.ts — máy chia đơn Profile cho thợ chụp / trang điểm (spec 27/08/2026).
 *
 * Endpoints:
 *   GET  /dispatch/my                      → suất của tôi + suất cần người (200)
 *   GET  /dispatch/board?branch=&date=     → bảng suất một cơ sở một ngày  (200)
 *   POST /dispatch/slots/{id}/return       → trả suất đang giữ            (200)
 *   POST /dispatch/slots/{id}/pickup       → tự nhặt một suất             (200)
 *   POST /dispatch/slots/{id}/ack          → bấm NHẬN suất được mời       (200)
 *   POST /dispatch/slots/{id}/decline      → từ chối suất được mời        (200)
 *   GET  /dispatch/points/me               → điểm hành vi của tôi         (200)
 *   GET  /dispatch/my-why?date=            → vì sao tôi được/chưa được suất (200)
 *   GET  /dispatch/points?branch=          → bảng điểm cả cơ sở           (200)
 *   GET  /dispatch/staff-points/{id}        → sổ điểm một nhân sự          (200)
 *   POST /dispatch/points/{id}/void        → gỡ một dòng điểm             (200)
 *   POST /dispatch/slots/{id}/override     → gán tay / đổi gán            (200)
 *   GET  /dispatch/precheck                → ba cảnh báo mềm              (200)
 *   GET  /dispatch/config                  → cấu hình máy chia            (200)
 *   PUT  /dispatch/config                  → ghi trọn gói cấu hình        (200)
 *   PUT  /dispatch/auto-accept             → CTV bật/tắt tự động nhận      (200)
 *   POST /dispatch/sweep                   → rà tay một ngày         (204, RỖNG)
 *   GET  /dispatch/lead-now                → tôi có đang là ca trưởng không (200)
 *
 * ⚠️ KHÁC store-board: cả ba mutation SUẤT ở đây trả 200 KÈM THÂN là
 * `DispatchSlot` vừa đổi. Lời cảnh báo "endpoint 204 không có thân, thêm
 * `|| !data` là mọi lần bấm thành công đều ném lỗi" ở đầu store-board/api.ts
 * KHÔNG áp dụng cho chúng — ở đó kiểm `!data` là ĐÚNG.
 *
 * ⚠️ NHƯNG /dispatch/sweep thì CÓ: nó trả 204 rỗng, nên chỗ đó chỉ kiểm
 * `error`. Một file hai luật — đọc kỹ từng hàm.
 */

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api/client";
import { serverErrorCode, serverErrorMessage } from "@/lib/api/error-message";
import { useShiftLeadStore } from "@/lib/auth/shift-lead";
import { z } from "zod";

import type { OverrideReasonCode } from "./overrideReasons";

import {
  DispatchBoardViewSchema,
  DispatchConfigSchema,
  DispatchDesigneeSchema,
  DispatchLineRuleSchema,
  DispatchMyViewSchema,
  DispatchMyWhySchema,
  DispatchPointRowSchema,
  DispatchPointsStaffViewSchema,
  DispatchPointsViewSchema,
  DispatchPointSummarySchema,
  DispatchPrecheckSchema,
  DispatchSimulationSchema,
  DispatchSlotSchema,
  DispatchSweepSummarySchema,
  OrderTagRuleSchema,
} from "./schemas";
import type {
  DispatchConfig,
  DispatchDept,
  DispatchPrecheck,
  DispatchSlotKind,
} from "./schemas";

export const dispatchKeys = {
  all: ["dispatch"] as const,
  // `brand` NẰM TRONG khoá, cùng lý do với `special` ở bảng suất ngay dưới.
  my: (brand = "") => ["dispatch", "my", brand] as const,
  // Tách theo CẢ cơ sở lẫn ngày: bảng là ảnh chụp của một ngày, dùng chung
  // khoá giữa hai ngày là bấm sang mai vẫn thấy bảng hôm nay.
  // `special` NẰM TRONG khoá: bật/tắt bộ lọc là hai tập suất khác nhau, dùng
  // chung khoá thì bấm lọc xong vẫn thấy bảng đầy đủ cho tới lần nạp sau —
  // đúng kiểu "lọc không ăn thua" mà không ai tìm ra vì mạng vẫn 200.
  board: (branch: string, date: string, special: boolean, brand = "") =>
    ["dispatch", "board", branch, date, special ? "special" : "all", brand] as const,
  // `date` NẰM TRONG khoá: đổi ngày ở ô chọn là một truy vấn khác — dùng chung
  // khoá là bấm sang ngày mai vẫn đọc lý do của hôm nay. "" = hôm nay của máy chủ.
  myWhy: (date = "") => ["dispatch", "my-why", date] as const,
  config: ["dispatch", "config"] as const,
  leadNow: ["dispatch", "lead-now"] as const,
};

const DispatchLeadNowSchema = z.object({
  leads: z.array(
    z.object({
      branch: z.string(),
      date: z.string(),
      shiftType: z.enum(["sang", "chieu"]),
      until: z.string(),
    }),
  ),
});

/**
 * Nạp "tôi có đang là ca trưởng ca nào không" vào kho `useShiftLeadStore`
 * (quyền trưởng ca gắn theo ca, CEO 27/09/2026). Gắn ở `useSidebarData` nên có
 * mặt trên mọi màn sau đăng nhập; menu và Bảng suất đọc kho đó.
 *
 * Nạp lại 2 phút một lần: trưởng cửa hàng đổi ca trưởng giữa ca thì người mới
 * thấy mục "Bảng suất cơ sở" trong vài phút, người cũ mất nút trong vài phút —
 * máy chủ thì từ chối người cũ NGAY.
 *
 * Lỗi mạng giữ nguyên câu trả lời cũ (không xoá kho): mất mạng thoáng qua
 * không được làm mất mục menu của người đang đứng ca.
 */
export function useLeadNowSync(enabled: boolean) {
  const setLeads = useShiftLeadStore((s) => s.setLeads);
  const q = useQuery({
    queryKey: dispatchKeys.leadNow,
    queryFn: async () => {
      const { data, error } = await api.GET("/dispatch/lead-now");
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không kiểm được ca trưởng"));
      }
      return DispatchLeadNowSchema.parse(data).leads;
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });
  const leads = q.data;
  useEffect(() => {
    if (!enabled) setLeads([]);
    else if (leads) setLeads(leads);
  }, [enabled, leads, setLeads]);
}

/**
 * Lỗi 409 của máy chia đơn.
 *
 * ⚠️ 409 ở đây KHÔNG có nghĩa "bấm force là xong". Cửa override có NĂM lối trả
 * 409 (`orderdispatch/service.go:1070/1077/1084/1106/1121`: suất đã đóng ·
 * tháng đã khoá lương · đơn đã huỷ · "cần xác nhận: ..." · suất vừa đóng trong
 * khoá) và **chỉ `:1106`** là `force: true` chữa được. Không phải nhận định:
 * cả gói `orderdispatch` đọc `in.Force` ĐÚNG MỘT LẦN, ngay tại điều kiện dẫn
 * tới `:1106` — bốn lối kia không thể đọc cờ ép dù muốn.
 *
 * Từ commit `592191b2` (Việc 12b) máy chủ **đã tách mã riêng** cho lối đó:
 * `dispatch_confirm_required`, thay vì `conflict` chung như bốn lối kia.
 *
 * ⛔ ĐỪNG "dọn" phép so `code` thành `status === 409`. Đó chính là con bug mà
 * mã riêng sinh ra để chặn: suy từ mã trạng thái là mọc nút "Vẫn gán" SAI
 * TRONG 4/5 CA — người dùng bấm, hỏng y nguyên, và không hiểu vì sao.
 * (Chú thích ở đây từng khẳng định "cả năm cùng code conflict, muốn phân biệt
 * thì backend phải cấp mã riêng trước". Đúng lúc viết, sai từ `592191b2`. Giữ
 * lại lời cảnh báo này vì bản cũ đã suýt dụ người sau gỡ đúng chỗ vừa vá.)
 *
 * Lớp này vẫn CỐ Ý không có cờ `needsForce` kiểu boolean: nó chở `code` thô,
 * còn người gọi tự so với hằng của mình (`OverrideDialog.CONFIRM_REQUIRED_CODE`).
 * Và tuyệt đối đừng dò chuỗi tiếng Việt để đoán — câu đó ai sửa lúc nào cũng được.
 *
 * Canh bởi `api.test.ts`: "409 ép được và 409 không ép được mang HAI mã khác
 * nhau" + "409 thiếu mã rơi về 'conflict' — ngã về phía KHÔNG mọc nút Vẫn gán".
 */
export class DispatchConflictError extends Error {
  readonly code: string;
  readonly status = 409;
  constructor(message: string, code: string) {
    super(message);
    this.name = "DispatchConflictError";
    this.code = code;
  }
}

/**
 * Dịch `error` của openapi-fetch thành Error giữ ĐÚNG câu tiếng Việt máy chủ
 * gửi (nuốt message là người dùng mù đường; lời tiếng Anh đi qua bộ dịch
 * chung), riêng 409 ra `DispatchConflictError` giữ mã máy chủ để màn hình
 * nhận diện bằng instanceof.
 */
export function toDispatchError(error: unknown, status: number, fallback: string): Error {
  const message = serverErrorMessage(error, fallback);
  if (status === 409) return new DispatchConflictError(message, serverErrorCode(error) || "conflict");
  return new Error(message);
}

/**
 * Suất của chính mình + mục "Suất cần người" nhặt được. Auth-only, mọi nhân
 * sự gọi được (khuôn /store-board/my).
 *
 * Nhịp 30 giây: suất được chia trước cả ngày, không phải hàng đợi tại quầy —
 * người dùng còn được đánh thức bằng push khi máy gán suất mới.
 */
export function useMyDispatch(brand = "") {
  return useQuery({
    queryKey: dispatchKeys.my(brand),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/my", {
        params: { query: brand ? { brand } : {} },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được suất của bạn");
      }
      return DispatchMyViewSchema.parse(data);
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Bảng suất của một cơ sở trong một ngày. Nhịp 15 giây — lễ tân phải thấy
 * suất chuyển sang ĐỎ (cần người) sớm để còn gọi người.
 *
 * Thiếu cơ sở hoặc ngày thì KHÔNG gọi: máy chủ trả 400 cho cả hai, gọi ra chỉ
 * để nhận lỗi rồi vẽ một câu đỏ vô nghĩa lên màn hình vừa mở.
 */
export function useDispatchBoard(branch: string, date: string, special = false, brand = "") {
  return useQuery({
    queryKey: dispatchKeys.board(branch, date, special, brand),
    queryFn: async () => {
      // Khoá `special` chỉ được CÓ MẶT khi bật. `exactOptionalPropertyTypes`
      // bắt đúng chỗ này: `special: undefined` không phải "bỏ khoá" mà là một
      // khoá mang giá trị undefined, và hợp đồng khai enum `"true"` duy nhất.
      const query = {
        branch,
        date,
        ...(special ? { special: "true" as const } : {}),
        ...(brand ? { brand } : {}),
      };
      const { data, error, response } = await api.GET("/dispatch/board", {
        params: { query },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được bảng suất");
      }
      return DispatchBoardViewSchema.parse(data);
    },
    staleTime: 10_000,
    refetchInterval: 15_000,
    enabled: branch !== "" && date !== "",
  });
}

/**
 * Mọi thao tác đều làm bảng cũ đi. Nạp lại luôn "orders" vì máy ghi thẳng ekip
 * vào đơn (một sổ duy nhất cho bảng lương) — màn lịch và đơn hàng đang mở phải
 * thấy tên người vừa đổi.
 */
function useDispatchMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dispatchKeys.all });
      void qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

/**
 * Trả suất đang giữ — máy chuyển ngay sang người kế, hoặc sang mục cần người.
 * Lý do BẮT BUỘC (máy chủ 400 nếu rỗng): nó vào sổ điểm phụ khi trả sát giờ.
 */
export function useReturnSlot() {
  return useDispatchMutation(async (args: { slotId: string; reason: string }) => {
    const { data, error, response } = await api.POST("/dispatch/slots/{id}/return", {
      params: { path: { id: args.slotId } },
      body: { reason: args.reason },
    });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không trả được suất");
    }
    return DispatchSlotSchema.parse(data);
  });
}

/** Tự nhặt một suất từ mục "Suất cần người". Không có thân yêu cầu. */
export function usePickupSlot() {
  return useDispatchMutation(async (args: { slotId: string }) => {
    const { data, error, response } = await api.POST("/dispatch/slots/{id}/pickup", {
      params: { path: { id: args.slotId } },
    });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không nhận được suất này");
    }
    return DispatchSlotSchema.parse(data);
  });
}

/**
 * Bấm NHẬN một suất được mời. Không có thân yêu cầu.
 *
 * 409 ở cửa này KHÔNG chữa được bằng cách bấm lại: suất đã hết hạn, đã sang
 * tay người khác, hoặc đã bị gỡ. Màn hình phải nạp lại danh sách chứ đừng
 * mọc nút "thử lại".
 */
export function useAckSlot() {
  return useDispatchMutation(async (args: { slotId: string }) => {
    const { data, error, response } = await api.POST("/dispatch/slots/{id}/ack", {
      params: { path: { id: args.slotId } },
    });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không nhận được suất này");
    }
    return DispatchSlotSchema.parse(data);
  });
}

/**
 * Từ chối một suất được mời. Lý do BẮT BUỘC (máy chủ 400 nếu rỗng) — nó đi
 * thẳng vào sổ điểm và vào lịch sử suất, là thứ quản lý đọc khi hỏi vì sao
 * đơn này chuyền tay ba lượt.
 */
export function useDeclineSlot() {
  return useDispatchMutation(async (args: { slotId: string; reason: string }) => {
    const { data, error, response } = await api.POST("/dispatch/slots/{id}/decline", {
      params: { path: { id: args.slotId } },
      body: { reason: args.reason },
    });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không từ chối được suất này");
    }
    return DispatchSlotSchema.parse(data);
  });
}

/**
 * Bật/tắt "tự động nhận suất máy giao" cho CHÍNH MÌNH (cộng tác viên). Không
 * gửi staffId: máy chủ lấy người từ vé đăng nhập; đặt hộ là việc của quản lý,
 * chưa có màn.
 */
export function useSetAutoAccept() {
  return useDispatchMutation(async (args: { enabled: boolean }) => {
    const { data, error, response } = await api.PUT("/dispatch/auto-accept", {
      body: { enabled: args.enabled },
    });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không đổi được chế độ tự động nhận");
    }
    return z.object({ enabled: z.boolean() }).parse(data);
  });
}

/**
 * Điểm hành vi của CHÍNH MÌNH. Không tự nạp lại theo nhịp: điểm chỉ đổi khi
 * chính người này bấm nhận/từ chối/trả — mà mọi mutation ở đây đã làm cũ cả
 * `dispatchKeys.all`.
 */
export function useMyPoints() {
  return useQuery({
    queryKey: [...dispatchKeys.all, "points", "me"] as const,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/points/me");
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được điểm của bạn");
      }
      return DispatchPointsViewSchema.parse(data);
    },
    staleTime: 60_000,
  });
}

/**
 * "Vì sao tôi chưa được suất?" của CHÍNH MÌNH trong một ngày (QĐ 0142 §7.2).
 *
 * `date` rỗng thì KHÔNG gửi khoá (máy chủ tự lấy hôm nay giờ Việt Nam) — tự
 * tính ngày ở máy khách là mở nhầm ngày khi điện thoại để sai múi giờ. Không
 * tự nạp lại theo nhịp: bảng thứ tự máy chủ đọc là ảnh chụp lúc xếp, không đổi
 * theo phút; mọi mutation của trang vẫn làm cũ `dispatchKeys.all`.
 */
export function useMyWhy(date = "") {
  return useQuery({
    queryKey: dispatchKeys.myWhy(date),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/my-why", {
        params: { query: date ? { date } : {} },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được lý do xếp suất");
      }
      return DispatchMyWhySchema.parse(data);
    },
    staleTime: 60_000,
  });
}

/** Bảng điểm cả cơ sở — của quản lý. */
export function useDispatchPoints(branch: string) {
  return useQuery({
    queryKey: [...dispatchKeys.all, "points", branch] as const,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/points", {
        params: { query: { branch } },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được bảng điểm");
      }
      // `.rows` chứ không phải `data`: cửa này BỌC kết quả. `z.parse` nhận
      // `unknown` nên viết nhầm KHÔNG làm tsc kêu — chỉ ném lúc chạy, và cả
      // bảng điểm chết trong khi mạng vẫn 200. Bài kiểm chạy thật ở
      // `api.rows.test.tsx` giữ chỗ này.
      return z.array(DispatchPointSummarySchema).parse(data.rows);
    },
    staleTime: 30_000,
    enabled: branch !== "",
  });
}

/** Sổ TỪNG DÒNG của một nhân sự — mở khi quản lý bấm vào một người. */
export function useStaffPoints(staffId: string) {
  return useQuery({
    queryKey: [...dispatchKeys.all, "points", "staff", staffId] as const,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/staff-points/{id}", {
        params: { path: { id: staffId } },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được sổ điểm của người này");
      }
      return DispatchPointsStaffViewSchema.parse(data);
    },
    staleTime: 30_000,
    enabled: staffId !== "",
  });
}

/**
 * Gỡ một dòng điểm. Máy chủ GHI DÒNG BÙ chứ không xoá dòng cũ, nên sau khi gỡ
 * cả hai dòng đều còn trong sổ — màn hình phải hiểu điều đó, đừng chờ dòng cũ
 * biến mất. Lý do BẮT BUỘC (máy chủ 400 nếu rỗng).
 */
export function useVoidPoint() {
  return useDispatchMutation(async (args: { pointId: string; note: string }) => {
    const { data, error, response } = await api.POST("/dispatch/points/{id}/void", {
      params: { path: { id: args.pointId } },
      body: { note: args.note },
    });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không gỡ được dòng điểm này");
    }
    return DispatchPointRowSchema.parse(data);
  });
}

/**
 * Gán tay / đổi gán. `staffId` là người ĐƯỢC GÁN (gán hộ người khác là chủ
 * đích); người THAO TÁC máy chủ tự lấy từ JWT, không bao giờ từ body.
 *
 * `force` chỉ vượt được điều kiện MỀM (trùng lịch / ngoài ca / quá trần) —
 * điều kiện CỨNG (khác khối, nghỉ việc, ngoài phạm vi cơ sở) luôn chặn. Xem
 * chú thích `DispatchConflictError` về chuyện 409 không tự nói cho ta biết
 * mình đang gặp loại nào.
 */
export function useOverrideSlot() {
  return useDispatchMutation(
    async (args: {
      slotId: string;
      staffId: string;
      reason: string;
      /** Mã trong danh mục đóng `OVERRIDE_REASONS` (QĐ 0209). */
      reasonCode?: OverrideReasonCode;
      force?: boolean;
    }) => {
      const { data, error, response } = await api.POST("/dispatch/slots/{id}/override", {
        params: { path: { id: args.slotId } },
        // Bỏ hẳn khoá khi không có, đừng gửi `undefined` — dự án bật
        // exactOptionalPropertyTypes nên "có khoá mà rỗng" là kiểu khác hẳn.
        body: {
          staffId: args.staffId,
          reason: args.reason,
          ...(args.reasonCode ? { reasonCode: args.reasonCode } : {}),
          ...(args.force === undefined ? {} : { force: args.force }),
        },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không gán được người");
      }
      return DispatchSlotSchema.parse(data);
    },
  );
}

/**
 * Soi một người cho một vai trước khi gán tay. Ba cảnh báo MỀM, KHÔNG phải
 * cổng kiểm hợp lệ: người ngoài nhóm ứng viên (khác khối, khác cơ sở) trả về
 * "sạch" vì phần chặn cứng thuộc về Override. Đừng hiện thành "đã kiểm ✓".
 *
 * Là hàm gọi thẳng (không phải hook) vì nơi dùng là đường gán tay CŨ ở màn
 * lịch — gọi ngay trước khi PUT, không phải lúc vẽ.
 */
export async function fetchPrecheck(
  orderId: string,
  slot: DispatchSlotKind,
  staffId: string,
): Promise<DispatchPrecheck> {
  const { data, error, response } = await api.GET("/dispatch/precheck", {
    params: { query: { orderId, slot, staffId } },
  });
  if (error || !data) {
    throw toDispatchError(error, response.status, "Không soi được lịch của người này");
  }
  return DispatchPrecheckSchema.parse(data);
}

/**
 * Cấu hình máy chia đơn — màn cài đặt của admin/giám đốc.
 *
 * `enabled` MẶC ĐỊNH true: màn cài đặt riêng đã gác quyền ngay ở cổng vào nên
 * gọi thẳng không tham số. Truyền false khi khối gọi nằm trong một màn mà
 * người xem có thể KHÔNG có quyền — backend gác `/dispatch/config` bằng `auth.Allow`
 * THUẦN (không AllowOrDept như /board): người không có quyền mà gọi chỉ nhận
 * 403 rồi vẽ một câu đỏ vô nghĩa lên màn hình vừa mở.
 *
 * KHÔNG có refetchInterval: cấu hình đổi vài lần một năm, không phải bảng
 * suất đổi từng phút.
 */
/** Máy chia (QĐ 0257): 1 = Chụp Ảnh + Travel, 2 = Ảnh Thẻ + Profile. */
export type DispatchMachine = 1 | 2;

/** Vắng tham số máy = máy 1: màn cũ gọi y như trước, không gửi `machine`. */
export function useDispatchConfig(enabled = true, machine: DispatchMachine = 1) {
  return useQuery({
    queryKey: [...dispatchKeys.config, machine] as const,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/config", {
        params: { query: machine === 2 ? { machine } : {} },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được cấu hình máy chia đơn");
      }
      return DispatchConfigSchema.parse(data);
    },
    staleTime: 60_000,
    enabled,
  });
}

/**
 * Ghi cấu hình. PUT là thay TRỌN GÓI — người gọi phải gửi đủ MỌI field, gửi
 * thiếu là lặng lẽ reset tham số mình không đụng tới.
 *
 * 400 ở đây KHÔNG phải lỗi mạng mà là backend từ chối một giá trị nó sẽ ngầm
 * sửa khác đi (capPerDay/scoreWindowDays/awardWindowDays dưới 1, bayesK âm),
 * kèm TÊN FIELD trong message — nên phải hiện nguyên văn cho người dùng, đừng
 * thay bằng câu chung chung.
 */
export function useSaveDispatchConfig(machine: DispatchMachine = 1) {
  return useDispatchMutation(async (body: DispatchConfig) => {
    const { data, error, response } = await api.PUT("/dispatch/config", {
      params: { query: machine === 2 ? { machine } : {} },
      body,
    });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không lưu được cấu hình");
    }
    return DispatchConfigSchema.parse(data);
  });
}

/**
 * Gửi bản nháp đi chạy thử với 90 ngày qua. Máy chủ ghi sổ rồi chạy nền, trả
 * lượt "queued" ngay (202). 409 = người này còn một lượt chưa xong; 400 nêu
 * ĐÚNG TÊN FIELD (cùng cửa kiểm với lưu) — hiện nguyên văn.
 */
export function useRunSimulation(machine: DispatchMachine = 1) {
  return useMutation({
    mutationFn: async (config: DispatchConfig) => {
      const { data, error, response } = await api.POST("/dispatch/simulations", {
        body: { config, windowDays: 90, ...(machine === 2 ? { machine } : {}) },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không chạy thử được");
      }
      return DispatchSimulationSchema.parse(data);
    },
  });
}

/** Một lượt chạy thử; hỏi lại mỗi 2 giây khi lượt còn mở, xong/hỏng thì thôi. */
export function useSimulation(id: string) {
  return useQuery({
    queryKey: [...dispatchKeys.all, "simulations", id] as const,
    enabled: id !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/simulations/{id}", {
        params: { path: { id } },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không đọc được lượt chạy thử");
      }
      return DispatchSimulationSchema.parse(data);
    },
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "queued" || s === "running" ? 2000 : false;
    },
  });
}

const lineRulesKey = [...dispatchKeys.all, "line-rules"] as const;

/** Cờ chấm công của từng dòng. Cửa BỌC `{rows}`. */
export function useLineRules(enabled = true) {
  return useQuery({
    queryKey: lineRulesKey,
    enabled,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/line-rules", {});
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được luật dòng sản phẩm");
      }
      return z.array(DispatchLineRuleSchema).parse(data.rows);
    },
  });
}

/**
 * Đổi cờ MỘT dòng — có hiệu lực ngay; máy chủ trả lại cả bảng. Gửi khoá nào
 * đổi khoá đó, vắng là không đụng tới; `jobKind` rỗng = xoá loại việc (QĐ 0257).
 */
export function useSetLineRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; requiresCheckin?: boolean; jobKind?: string }) => {
      const { data, error, response } = await api.PUT("/dispatch/line-rules", { body: input });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không đổi được luật dòng");
      }
      return z.array(DispatchLineRuleSchema).parse(data.rows);
    },
    onSuccess: (rows) => qc.setQueryData(lineRulesKey, rows),
  });
}

/** Bảng người được chỉ định của một cơ sở. Cửa BỌC `{rows}`. */
export function useDesignees(branch: string) {
  return useQuery({
    queryKey: [...dispatchKeys.all, "designees", branch] as const,
    enabled: branch !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dispatch/designees", {
        params: { query: { branch } },
      });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được danh sách chỉ định");
      }
      return z.array(DispatchDesigneeSchema).parse(data.rows);
    },
  });
}

/**
 * Thay TRỌN GÓI danh sách của bộ ba (cơ sở, nhãn, khối việc) — gửi mảng rỗng
 * là gỡ hết. Nhãn cần chỉ định mà không còn ai thì mọi suất của đơn mang nhãn
 * đó rơi vào "cần người": cố ý đỏ rõ ràng, hơn là máy lặng lẽ gán bừa.
 */
export function useSetDesignees() {
  return useDispatchMutation(
    async (input: { branch: string; tagCode: string; dept: DispatchDept; staffIds: string[] }) => {
      const { data, error, response } = await api.PUT("/dispatch/designees", { body: input });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không lưu được chỉ định");
      }
      return z.array(DispatchDesigneeSchema).parse(data.rows);
    },
  );
}

/**
 * Danh mục nhãn, KỂ CẢ nhãn đã tắt (màn cài đặt phải bật lại được). Cửa BỌC
 * `{rows}`. Khoá cache đứng RIÊNG khỏi nhánh "dispatch": nhãn dùng chung với
 * màn đơn hàng, không phải tài sản của máy chia.
 */
export function useOrderTags() {
  return useQuery({
    queryKey: orderTagKeys.all,
    staleTime: 300_000,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/order-tags");
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không tải được danh mục nhãn");
      }
      return z.array(OrderTagRuleSchema).parse(data.rows);
    },
  });
}

export const orderTagKeys = { all: ["order-tags"] as const };

/**
 * Thêm-hoặc-đè một nhãn. MỘT cửa duy nhất, mã nhãn đi trong THÂN chứ không
 * trong đường dẫn — mã nhãn là emoji kèm tiếng Việt có dấu, nhét vào URL là tự
 * chuốc thêm một tầng mã hoá. Đừng đi tìm `PUT /order-tags/{code}`: không có.
 *
 * Cờ `virtual` KHÔNG gửi lên: nó là cờ của hệ, máy chủ lấy từ dòng cũ.
 * `retouchHardJob` chỉ gửi khi đổi CHÍNH ô đó — không gửi thì máy chủ giữ
 * nguyên, nên lưu tên hay bật tắt nhãn không đè cờ ca khó bằng bản cũ trong bộ
 * nhớ đệm của màn.
 */
export function useSaveOrderTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      code: string;
      label: string;
      needsDesignee: boolean;
      active: boolean;
      sort: number;
      retouchHardJob?: boolean;
    }) => {
      const { data, error, response } = await api.POST("/order-tags", { body: input });
      if (error || !data) {
        throw toDispatchError(error, response.status, "Không lưu được nhãn");
      }
      return OrderTagRuleSchema.parse(data);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: orderTagKeys.all }),
  });
}

/**
 * Rà lại suất của một ngày, chạy tay. Dùng ngay sau khi vừa đổi lịch hoặc vừa
 * chỉ định thêm người — không phải chờ lượt rà tự động của đêm.
 *
 * ⚠️ Cửa này trả 200 KÈM THÂN (tóm tắt ngày vừa rà), KHÔNG phải 204 như bản
 * trước: người bấm phải thấy ngay ngày đó còn bao nhiêu suất chưa xong. Nên ở
 * đây `|| !data` là ĐÚNG — bỏ nó đi thì một lượt rà trả thân rỗng lọt qua im
 * lặng và màn hình hiện số 0 như thể mọi việc đã xong.
 *
 * Đi qua useDispatchMutation nên bảng suất tự nạp lại — không phải F5.
 */
export function useSweepDispatch() {
  return useDispatchMutation(async (date: string) => {
    const { data, error, response } = await api.POST("/dispatch/sweep", { body: { date } });
    if (error || !data) {
      throw toDispatchError(error, response.status, "Không rà lại được");
    }
    return DispatchSweepSummarySchema.parse(data);
  });
}
